import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { Pill, SurfacePageCard, Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { formatDayMonth } from '@/versions/v1/shared/clock.derive';
import {
  EXCURSION_WORKFLOW_NAME,
  EXCURSION_WORKFLOW_TRIGGER,
  investigationRunStatusLabel,
  runStepCount,
  type InvestigationRunRow,
} from '@/versions/v1/modules/advisory/InvestigationRun.transform';
import { useInvestigationRunRows } from '@/versions/v1/modules/advisory/useInvestigationRunRows';

function statusTone(status: InvestigationRunRow['status']): 'success' | 'warning' | 'default' {
  if (status === 'approved') return 'success';
  if (status === 'running') return 'warning';
  return 'default';
}

/**
 * K15 / D24: the Excursion investigation workflow's card — its definition,
 * its read-only tools read from the K6 recording, and every run, each linking
 * back to the case page's Investigation column. Since K19 the plant project's
 * Workflows page lists it in the full catalogue (`WorkflowCatalogue`); this
 * card stays on the foundation page of the other demo projects.
 */
export function InvestigationWorkflowCatalogue({ projectId }: { projectId: string }) {
  const { runs, tools } = useInvestigationRunRows();

  return (
    <SurfacePageCard className="space-y-3 p-5" data-investigation-catalogue>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-semibold text-ink-primary">{EXCURSION_WORKFLOW_NAME}</h2>
        <Pill
          tone="default"
          className="gap-1 border-[color-mix(in_srgb,var(--color-accent-500)_35%,transparent)] bg-[var(--accent-soft)] px-1.5 text-[10px] text-accent-700 dark:text-accent-300"
          data-actor="agent"
        >
          <Sparkles className="size-3" aria-hidden="true" />
          Agent
        </Pill>
        <span className="text-xs text-muted" data-investigation-workflow-trigger>Trigger · {EXCURSION_WORKFLOW_TRIGGER}</span>
      </div>

      {tools ? (
        <p className="text-xs leading-relaxed text-muted" data-investigation-workflow-tools>
          <span className="font-medium text-ink-secondary">Read-only tools</span>
          {` · ${tools}`}
        </p>
      ) : null}

      {runs.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-[var(--hairline)]">
          <Table wrap={false} className="text-left">
            <THead className="border-b border-[var(--hairline)] bg-transparent">
              <TR><TH className="px-3 py-2">Run</TH><TH>Case</TH><TH>Started</TH><TH>Status</TH><TH>Steps</TH><TH className="text-right">Trace</TH></TR>
            </THead>
            <TBody className="divide-y divide-[var(--hairline)]" data-investigation-catalogue-runs>
              {runs.map((run) => (
                <TR key={run.id} data-investigation-catalogue-run={run.id}>
                  <TD className="px-3 py-2 font-mono text-xs text-ink-primary">{run.id}</TD>
                  <TD className="font-mono text-xs">{run.caseCode}</TD>
                  <TD className="tabular-nums text-xs text-muted">
                    {formatDayMonth(run.startedAt)} {run.startedAt.slice(11, 16)}
                  </TD>
                  <TD>
                    <Pill tone={statusTone(run.status)} className="px-1.5 text-[10px]">
                      {investigationRunStatusLabel(run.status)}
                    </Pill>
                  </TD>
                  <TD className="tabular-nums text-xs">{runStepCount(run.steps)}</TD>
                  <TD className="text-right">
                    <Link
                      to={`/projects/${projectId}/advisory/case/${run.caseId}?run=${run.id}`}
                      className="text-xs font-medium text-accent-700 hover:underline dark:text-accent-400"
                      data-investigation-catalogue-link={run.id}
                    >
                      open trace
                    </Link>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      ) : null}
    </SurfacePageCard>
  );
}
